import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { t } from '../i18n';
import { api, fileUrl } from '../lib/api';
import { useDocument, useDocuments } from '../lib/queries';
import { fmtDate, fmtDateTime, fmtMoney } from '../lib/format';
import type { DocDetail, QuoteStatus } from '../lib/types';
import { Button, Callout, Confidence, Dialog, ErrorState, Field, Input, Ltr, PaymentBadge, QuoteBadge, Select, Textarea, useConfirm, useToast } from './ui';
import { CustomerPicker } from './CustomerPicker';
import { MarkPaidDialog, invalidatePayments } from './MarkPaid';
import { IconDownload, IconExternal, IconRepeat, IconStar, IconTrash, IconX } from './icons';
import { NewOrderButton } from './Orders';
import { useCan } from '../lib/auth';

export function DocumentDrawer({ id, onClose }: { id: number; onClose: () => void }) {
  const { data: doc, isLoading, error, refetch } = useDocument(id);
  const qc = useQueryClient();
  const toast = useToast();
  const confirm = useConfirm();
  const [editing, setEditing] = useState(false);
  const [reassigning, setReassigning] = useState(false);
  const [allocating, setAllocating] = useState(false);
  const [marking, setMarking] = useState(false);
  const can = useCan();

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && !editing && !reassigning && !allocating && onClose();
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose, editing, reassigning, allocating]);

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ['document', id] });
    qc.invalidateQueries({ queryKey: ['documents'] });
    qc.invalidateQueries({ queryKey: ['customer'] });
    qc.invalidateQueries({ queryKey: ['customers'] });
    qc.invalidateQueries({ queryKey: ['dashboard'] });
    qc.invalidateQueries({ queryKey: ['cashflow'] });
  };

  const patch = useMutation({
    mutationFn: (body: Record<string, unknown>) => api<DocDetail>(`/api/documents/${id}`, { method: 'PATCH', body }),
    onSuccess: () => {
      invalidate();
      toast.success(t.common.savedToast);
    },
    onError: toast.error,
  });

  const remove = async () => {
    if (!(await confirm({ title: t.docs.deleteConfirm, danger: true, confirmLabel: t.common.delete }))) return;
    try {
      await api(`/api/documents/${id}`, { method: 'DELETE' });
      invalidate();
      toast.success(t.common.deletedToast);
      onClose();
    } catch (e) {
      toast.error(e);
    }
  };

  return createPortal(
    <>
      <div className="drawer-overlay" onClick={onClose} />
      <aside className="drawer" role="dialog" aria-modal="true" aria-label={doc ? `${doc.typeLabel} ${doc.number ?? ''}` : t.common.loading}>
        <div className="dialog-head">
          {doc ? (
            <div style={{ minWidth: 0 }}>
              <h2 className="ellipsis">
                {doc.typeLabel} <Ltr className="tabular">{doc.number ?? ''}</Ltr>
              </h2>
              {doc.customerId ? (
                <Link to={`/customers/${doc.customerId}`} className="small" onClick={onClose}>
                  {doc.customerName}
                </Link>
              ) : (
                <span className="small muted">לא משויך ללקוח</span>
              )}
            </div>
          ) : (
            <span className="skeleton" style={{ width: 180 }} />
          )}
          <span className="spacer" />
          {doc?.customerId && can('orders.edit') && ['quote', 'tax_invoice', 'tax_invoice_receipt', 'delivery_note'].includes(doc.type) && doc.lineItems?.length ? (
            <NewOrderButton customerId={doc.customerId} sourceDocumentId={doc.id} size="sm">
              <IconRepeat /> הזמן שוב
            </NewOrderButton>
          ) : null}
          {doc?.file ? (
            <a className="btn sm" href={fileUrl(doc.file.id, true)}>
              <IconDownload /> {t.common.download}
            </a>
          ) : null}
          {doc?.ingestion?.gmailUrl ? (
            <a className="btn sm" href={doc.ingestion.gmailUrl} target="_blank" rel="noreferrer noopener">
              <IconExternal /> {t.docs.openEmail}
            </a>
          ) : null}
          <Button size="sm" variant="ghost" icon onClick={onClose} aria-label={t.common.close}>
            <IconX />
          </Button>
        </div>

        {isLoading ? (
          <div className="panel-body"><span className="skeleton" style={{ width: '60%' }} /></div>
        ) : error || !doc ? (
          <ErrorState error={error} onRetry={() => refetch()} />
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1.1fr)', flex: 1, minHeight: 0 }} className="doc-layout">
            {/* details */}
            <div style={{ overflowY: 'auto', borderInlineEnd: '1px solid var(--line)' }}>
              <div className="panel-body stack">
                <div className="row wrap">
                  {doc.type === 'quote' ? <QuoteBadge status={doc.quoteStatus} /> : <PaymentBadge status={doc.paymentStatus} />}
                  {doc.isPrimaryQuote ? <span className="badge accent"><IconStar size={12} filled /> {t.docs.primary}</span> : null}
                  <span className="badge outline">{t.enums.source[doc.source]}</span>
                  {doc.inMorning ? (
                    <span className={`badge ${doc.emailVerified ? 'ok' : 'outline'}`} title={doc.emailVerified ? 'המסמך קיים במורנינג והתקבל גם במייל' : 'המסמך קיים במורנינג; עותק במייל עדיין לא התקבל'}>
                      {doc.emailVerified ? 'מורנינג ✓ אומת במייל' : 'מורנינג'}
                    </span>
                  ) : null}
                  {doc.morningStatus === 2 ? <span className="badge outline" title="החשבונית סומנה במורנינג כסגורה ידנית">נסגרה ידנית במורנינג</span> : null}
                  <span className="spacer" />
                  <Button size="sm" onClick={() => setEditing(true)}>{t.common.edit}</Button>
                  <Button size="sm" onClick={() => setReassigning(true)}>{t.docs.reassign}</Button>
                  <Button size="sm" variant="ghost" icon onClick={remove} aria-label={t.common.delete} title={t.common.delete}>
                    <IconTrash />
                  </Button>
                </div>

                {doc.type === 'quote' ? (
                  <div className="row wrap">
                    <Select
                      aria-label={t.docs.cols.status}
                      style={{ width: 160 }}
                      value={doc.quoteStatus ?? 'sent'}
                      onChange={(e) => patch.mutate({ quoteStatus: e.target.value as QuoteStatus })}
                    >
                      {(Object.keys(t.enums.quoteStatus) as QuoteStatus[]).map((s) => (
                        <option key={s} value={s}>{t.enums.quoteStatus[s]}</option>
                      ))}
                    </Select>
                    {!doc.isPrimaryQuote ? (
                      <Button size="sm" onClick={() => patch.mutate({ isPrimaryQuote: true })} loading={patch.isPending}>
                        <IconStar /> {t.docs.setPrimary}
                      </Button>
                    ) : null}
                  </div>
                ) : null}

                <dl className="kv">
                  <dt>{t.docs.cols.date}</dt>
                  <dd className="tabular">{fmtDate(doc.issueDate)}</dd>
                  {doc.type === 'tax_invoice' || doc.type === 'proforma' ? (
                    <>
                      <dt>{t.docs.cols.due}</dt>
                      <dd className="tabular">
                        {fmtDate(doc.dueDate)} {doc.dueDateManual ? <span className="faint small">(ידני)</span> : <span className="faint small">(לפי תנאי התשלום)</span>}
                      </dd>
                      {doc.documentDueDate ? (
                        <>
                          <dt>לתשלום עד במסמך</dt>
                          <dd className="tabular">
                            {fmtDate(doc.documentDueDate)}
                            {doc.documentDueDate !== doc.dueDate ? <span className="faint small"> · שונה מתנאי התשלום של הלקוח</span> : null}
                          </dd>
                        </>
                      ) : null}
                    </>
                  ) : null}
                  <dt>{t.docs.cols.subtotal}</dt>
                  <dd className="tabular">{fmtMoney(doc.subtotal)}</dd>
                  <dt>{t.docs.cols.vat}</dt>
                  <dd className="tabular">{fmtMoney(doc.vat)}</dd>
                  <dt>{t.docs.cols.total}</dt>
                  <dd className="tabular strong">{fmtMoney(doc.total)}</dd>
                  {/* `paid` includes credits; they cancel the invoice rather than pay it, so they get their own line. */}
                  {doc.credited ? (
                    <>
                      <dt>זוכה (בוטל)</dt>
                      <dd className="tabular">{fmtMoney(doc.credited)}</dd>
                    </>
                  ) : null}
                  {doc.paid != null && doc.paymentStatus !== 'cancelled' ? (
                    <>
                      <dt>שולם</dt>
                      <dd className="tabular">{fmtMoney(doc.paid - (doc.credited ?? 0))} {doc.open ? <span className="muted">· יתרה {fmtMoney(doc.open)}</span> : null}</dd>
                    </>
                  ) : null}
                  {doc.description ? (
                    <>
                      <dt>{t.docs.cols.description}</dt>
                      <dd>{doc.description}</dd>
                    </>
                  ) : null}
                  {doc.deliveryAddress ? (
                    <>
                      <dt>{t.docs.cols.address}</dt>
                      <dd>{doc.deliveryAddress}</dd>
                    </>
                  ) : null}
                  {doc.notes ? (
                    <>
                      <dt>{t.common.notes}</dt>
                      <dd style={{ whiteSpace: 'pre-wrap' }}>{doc.notes}</dd>
                    </>
                  ) : null}
                  {doc.type === 'delivery_note' ? (
                    <>
                      <dt>{t.docs.cols.billed}</dt>
                      <dd>
                        {doc.billedBy ? <DocLink id={doc.billedBy.id} label={`חשבונית ${doc.billedBy.number ?? ''}`} /> : <span className="badge warn">{t.docs.notBilled}</span>}
                        {doc.billedBy && doc.billedManually ? (
                          <>
                            {' '}
                            <span className="small muted">(קושר ידנית)</span>{' '}
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={async () => {
                                try {
                                  await api(`/api/billing/link/${id}`, { method: 'DELETE' });
                                  invalidate();
                                  qc.invalidateQueries({ queryKey: ['billing'] });
                                  toast.success('הקישור בוטל');
                                } catch (e) {
                                  toast.error(e);
                                }
                              }}
                            >
                              ביטול קישור
                            </Button>
                          </>
                        ) : null}
                      </dd>
                    </>
                  ) : null}
                </dl>

                {doc.deliveryNotes.length ? (
                  <div>
                    <h3 style={{ marginBottom: 6 }}>תעודות משלוח שחויבו בחשבונית זו</h3>
                    <div className="row wrap">
                      {doc.deliveryNotes.map((d) => (
                        <DocLink key={d.id} id={d.id} label={`תעודת משלוח ${d.number ?? ''}`} />
                      ))}
                    </div>
                  </div>
                ) : null}

                {doc.paymentStatus === 'cancelled' ? (
                  <Callout tone="warn" title="החשבונית בוטלה בחשבונית זיכוי">
                    <span className="small">
                      {doc.allocationsIn
                        .filter((a) => a.type === 'credit_note')
                        .map((a) => `חשבונית זיכוי ${a.number ?? ''}`)
                        .join(', ')}
                      {' — '}החשבונית אינה נספרת במכירות, ביתרות הפתוחות ובאיחורים.
                    </span>
                  </Callout>
                ) : doc.type === 'tax_invoice' ? (
                  doc.paidMark ? (
                    <Callout
                      tone="ok"
                      title={`סומנה כשולמה ידנית${doc.paidMark.paidDate ? ` · ${fmtDate(doc.paidMark.paidDate)}` : ''}`}
                      action={
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={async () => {
                            if (!(await confirm({ title: 'לבטל את סימון "שולם"?', body: 'החשבונית תחזור להיות פתוחה.', confirmLabel: 'ביטול הסימון' }))) return;
                            try {
                              await api(`/api/documents/${id}/mark-paid`, { method: 'DELETE' });
                              invalidatePayments(qc);
                              toast.success('הסימון בוטל');
                            } catch (e) {
                              toast.error(e);
                            }
                          }}
                        >
                          ביטול סימון
                        </Button>
                      }
                    >
                      <span className="small">
                        {doc.paidMark.paymentMethodLabel ?? ''} · {fmtMoney(doc.paidMark.amount)}
                        {doc.paidMark.note ? ` · ${doc.paidMark.note}` : ''}
                        {doc.paidMark.userName ? <span className="muted"> · סומן ע״י {doc.paidMark.userName}</span> : null}
                      </span>
                    </Callout>
                  ) : (doc.open ?? 0) > 0.009 ? (
                    <div className="row">
                      <Button variant="primary" size="sm" onClick={() => setMarking(true)}>סמן כשולם</Button>
                      <span className="small muted">לתשלום שהתקבל בלי שהופקה קבלה במורנינג</span>
                    </div>
                  ) : null
                ) : null}

                <Payments doc={doc} onAllocate={() => setAllocating(true)} onChanged={invalidate} />

                {doc.lineItems.length ? (
                  <div>
                    <h3 style={{ marginBottom: 6 }}>{t.docs.lineItems}</h3>
                    <div className="panel">
                      <table className="table">
                        <thead>
                          <tr>
                            <th>פריט</th>
                            <th className="num">כמות</th>
                            <th className="num">מחיר</th>
                            <th className="num">{t.common.total}</th>
                          </tr>
                        </thead>
                        <tbody>
                          {doc.lineItems.map((li, i) => (
                            <tr key={i}>
                              <td style={{ whiteSpace: 'normal', padding: '8px 12px' }}>{li.description || '—'}</td>
                              <td className="num">{li.quantity ?? '—'}</td>
                              <td className="num">{fmtMoney(li.unitPrice)}</td>
                              <td className="num">{fmtMoney(li.total)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                ) : null}

                <div>
                  <h3 style={{ marginBottom: 6 }}>{t.docs.origin}</h3>
                  <dl className="kv small">
                    <dt>{t.docs.cols.source}</dt>
                    <dd>{t.enums.source[doc.source]}</dd>
                    <dt>{t.docs.cols.imported}</dt>
                    <dd className="tabular">{fmtDateTime(doc.importedAt)}</dd>
                    {doc.ingestion?.subject ? (
                      <>
                        <dt>נושא המייל</dt>
                        <dd>{doc.ingestion.subject}</dd>
                        <dt>התקבל</dt>
                        <dd className="tabular">{fmtDateTime(doc.ingestion.receivedAt)}</dd>
                      </>
                    ) : null}
                    {doc.ingestion?.confidence != null ? (
                      <>
                        <dt>זיהוי הלקוח</dt>
                        <dd>
                          <Confidence value={doc.ingestion.confidence} />
                          <div className="muted">{doc.ingestion.matchReasons.map((r) => r.text).join(' · ')}</div>
                        </dd>
                      </>
                    ) : null}
                    {doc.extractedCustomerName ? (
                      <>
                        <dt>השם במסמך</dt>
                        <dd>
                          {doc.extractedCustomerName}
                          {doc.extractedCompanyNumber ? <span className="muted"> · ח.פ <Ltr>{doc.extractedCompanyNumber}</Ltr></span> : null}
                        </dd>
                      </>
                    ) : null}
                  </dl>
                </div>
              </div>
            </div>
            {/* preview */}
            <div style={{ background: '#e9e9e5', minHeight: 0 }}>
              <FilePreview file={doc.file} />
            </div>
          </div>
        )}
      </aside>

      {doc ? (
        <>
          <EditDocumentDialog open={editing} doc={doc} onClose={() => setEditing(false)} onSave={(b) => patch.mutateAsync(b).then(() => setEditing(false))} />
          <ReassignDialog open={reassigning} doc={doc} onClose={() => setReassigning(false)} onSave={(customerId) => patch.mutateAsync({ customerId }).then(() => setReassigning(false))} />
          {allocating ? <AllocateDialog doc={doc} onClose={() => setAllocating(false)} onDone={invalidate} /> : null}
          {marking ? (
            <MarkPaidDialog invoices={[{ id: doc.id, number: doc.number, open: doc.open, customerName: doc.customerName }]} onClose={() => setMarking(false)} />
          ) : null}
        </>
      ) : null}
    </>,
    document.body,
  );
}

function DocLink({ id, label }: { id: number; label: string }) {
  return (
    <Link to={`?doc=${id}`} className="badge outline" style={{ textDecoration: 'none' }}>
      {label}
    </Link>
  );
}

export function FilePreview({ file }: { file: { id: number; mimeType: string; name?: string } | null }) {
  if (!file) return <div className="empty">{t.docs.noFile}</div>;
  if (file.mimeType.startsWith('image/')) {
    return (
      <div style={{ padding: 16, overflow: 'auto', height: '100%' }}>
        <img className="doc-image" src={fileUrl(file.id)} alt={file.name ?? ''} />
      </div>
    );
  }
  if (file.mimeType !== 'application/pdf') {
    // Excel quotes and other files the browser cannot show inline.
    return (
      <div className="empty">
        <h3>{file.name ?? 'קובץ'}</h3>
        <p>לא ניתן להציג את הקובץ כאן. פרטי המסמך מופיעים משמאל.</p>
        <a className="btn" href={fileUrl(file.id, true)}>הורדת הקובץ</a>
      </div>
    );
  }
  return <iframe className="doc-frame" title={file.name ?? 'document'} src={`${fileUrl(file.id)}#view=FitH`} />;
}

function Payments({ doc, onAllocate, onChanged }: { doc: DocDetail; onAllocate: () => void; onChanged: () => void }) {
  const toast = useToast();
  const isInvoice = doc.type === 'tax_invoice' || doc.type === 'tax_invoice_receipt';
  const isSource = doc.type === 'receipt' || doc.type === 'credit_note';
  if (!isInvoice && !isSource) return null;
  const rows = isInvoice ? doc.allocationsIn : doc.allocationsOut;
  const allocated = doc.allocationsOut.reduce((s, a) => s + (a.amount ?? 0), 0);
  const free = isSource ? Math.max(0, (doc.total ?? 0) - allocated) : 0;

  const removeAlloc = async (allocId: number) => {
    try {
      await api(`/api/allocations/${allocId}`, { method: 'DELETE' });
      onChanged();
    } catch (e) {
      toast.error(e);
    }
  };

  return (
    <div>
      <div className="row" style={{ marginBottom: 6 }}>
        <h3>{doc.paymentStatus === 'cancelled' ? 'בוטלה על ידי' : isInvoice ? t.docs.paidBy : doc.type === 'credit_note' ? 'מבטלת את' : t.docs.paysFor}</h3>
        <span className="spacer" />
        {isSource && free > 0.009 && doc.customerId ? (
          <Button size="sm" onClick={onAllocate}>{t.docs.allocate}</Button>
        ) : null}
      </div>
      {rows.length === 0 ? (
        <div className="muted small">{isInvoice ? t.docs.noPayments : 'המסמך לא שויך לחשבונית. אם הוא מתייחס לחשבונית — ניתן לשייך ידנית.'}</div>
      ) : (
        <div className="panel">
          <table className="table">
            <tbody>
              {rows.map((a) => (
                <tr key={a.id}>
                  <td>
                    <DocLink id={a.documentId} label={`${a.typeLabel} ${a.number ?? ''}`} />
                  </td>
                  <td className="muted small">{t.enums.allocation[a.method]}</td>
                  <td className="num">{fmtMoney(a.amount)}</td>
                  <td style={{ width: 40 }}>
                    {a.method === 'manual' ? (
                      <Button size="sm" variant="ghost" icon onClick={() => removeAlloc(a.id)} aria-label="הסרת שיוך">
                        <IconX size={14} />
                      </Button>
                    ) : null}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {isSource && free > 0.009 && rows.length > 0 ? <div className="small muted" style={{ marginTop: 6 }}>יתרה לא משויכת: {fmtMoney(free)}</div> : null}
    </div>
  );
}

function EditDocumentDialog({ open, doc, onClose, onSave }: { open: boolean; doc: DocDetail; onClose: () => void; onSave: (b: Record<string, unknown>) => Promise<unknown> }) {
  const [f, setF] = useState({
    number: doc.number ?? '',
    issueDate: doc.issueDate ?? '',
    dueDate: doc.dueDate ?? '',
    subtotal: doc.subtotal?.toString() ?? '',
    vat: doc.vat?.toString() ?? '',
    total: doc.total?.toString() ?? '',
    description: doc.description ?? '',
    deliveryAddress: doc.deliveryAddress ?? '',
    notes: doc.notes ?? '',
  });
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (open)
      setF({
        number: doc.number ?? '',
        issueDate: doc.issueDate ?? '',
        dueDate: doc.dueDate ?? '',
        subtotal: doc.subtotal?.toString() ?? '',
        vat: doc.vat?.toString() ?? '',
        total: doc.total?.toString() ?? '',
        description: doc.description ?? '',
        deliveryAddress: doc.deliveryAddress ?? '',
        notes: doc.notes ?? '',
      });
  }, [open, doc]);
  const num = (v: string) => (v.trim() === '' ? null : Number(v.replace(/,/g, '')));
  const set = (k: keyof typeof f) => (e: { target: { value: string } }) => setF({ ...f, [k]: e.target.value });
  const invalidNum = [f.subtotal, f.vat, f.total].some((v) => v.trim() !== '' && Number.isNaN(Number(v.replace(/,/g, ''))));

  const save = async () => {
    setBusy(true);
    try {
      const body: Record<string, unknown> = {
        number: f.number || null,
        issueDate: f.issueDate || null,
        subtotal: num(f.subtotal),
        vat: num(f.vat),
        total: num(f.total),
        description: f.description || null,
        notes: f.notes || null,
      };
      if (doc.type === 'delivery_note') body.deliveryAddress = f.deliveryAddress || null;
      if ((f.dueDate || null) !== doc.dueDate) body.dueDate = f.dueDate || null;
      await onSave(body);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={`${t.common.edit} ${doc.typeLabel}`}
      footer={
        <>
          <Button variant="primary" onClick={save} loading={busy} disabled={invalidNum}>{t.common.save}</Button>
          <Button onClick={onClose}>{t.common.cancel}</Button>
        </>
      }
    >
      <div className="form-grid">
        <Field label={t.docs.cols.number}>
          <Input value={f.number} onChange={set('number')} ltr />
        </Field>
        <Field label={t.docs.cols.date}>
          <Input type="date" value={f.issueDate} onChange={set('issueDate')} />
        </Field>
        {doc.type === 'tax_invoice' || doc.type === 'proforma' ? (
          <Field label={t.docs.cols.due} hint="שינוי ידני גובר על חישוב לפי תנאי התשלום" className="span-2">
            <Input type="date" value={f.dueDate} onChange={set('dueDate')} />
          </Field>
        ) : null}
        <Field label={t.docs.cols.subtotal}>
          <Input inputMode="decimal" value={f.subtotal} onChange={set('subtotal')} ltr />
        </Field>
        <Field label={t.docs.cols.vat}>
          <Input inputMode="decimal" value={f.vat} onChange={set('vat')} ltr />
        </Field>
        <Field label={t.docs.cols.total} error={invalidNum ? 'יש להזין מספרים בלבד' : null}>
          <Input inputMode="decimal" value={f.total} onChange={set('total')} ltr />
        </Field>
        {doc.type === 'delivery_note' ? (
          <Field label={t.docs.cols.address}>
            <Input value={f.deliveryAddress} onChange={set('deliveryAddress')} />
          </Field>
        ) : null}
        <Field label={t.docs.cols.description} className="span-2">
          <Input value={f.description} onChange={set('description')} />
        </Field>
        <Field label={t.common.notes} className="span-2">
          <Textarea value={f.notes} onChange={set('notes')} />
        </Field>
      </div>
    </Dialog>
  );
}

function ReassignDialog({ open, doc, onClose, onSave }: { open: boolean; doc: DocDetail; onClose: () => void; onSave: (customerId: number) => Promise<unknown> }) {
  const [target, setTarget] = useState<number | null>(null);
  const [busy, setBusy] = useState(false);
  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={t.docs.reassign}
      footer={
        <>
          <Button
            variant="primary"
            disabled={!target}
            loading={busy}
            onClick={async () => {
              setBusy(true);
              try {
                await onSave(target!);
              } finally {
                setBusy(false);
              }
            }}
          >
            העברה
          </Button>
          <Button onClick={onClose}>{t.common.cancel}</Button>
        </>
      }
    >
      <div className="stack">
        <p className="muted">
          {doc.typeLabel} <Ltr>{doc.number}</Ltr> משויך כעת ל<strong>{doc.customerName ?? '—'}</strong>. בחרו את הלקוח הנכון:
        </p>
        <CustomerPicker value={target} onChange={setTarget} exclude={doc.customerId ? [doc.customerId] : []} />
        <Callout tone="info">מועד התשלום יחושב מחדש לפי תנאי התשלום של הלקוח החדש, ושיוכי תשלום ידניים יוסרו.</Callout>
      </div>
    </Dialog>
  );
}

function AllocateDialog({ doc, onClose, onDone }: { doc: DocDetail; onClose: () => void; onDone: () => void }) {
  const toast = useToast();
  const { data } = useDocuments({ customerId: doc.customerId!, includeBranches: true, types: 'tax_invoice', payment: 'open', pageSize: 100, sort: 'issueDate', dir: 'asc' });
  const allocated = doc.allocationsOut.reduce((s, a) => s + (a.amount ?? 0), 0);
  const free = Math.max(0, Math.round(((doc.total ?? 0) - allocated) * 100) / 100);
  const [invoiceId, setInvoiceId] = useState<number | null>(null);
  const [amount, setAmount] = useState(String(free));
  const [busy, setBusy] = useState(false);
  const inv = data?.items.find((d) => d.id === invoiceId);
  const amt = Number(amount);
  const invalid = !invoiceId || !(amt > 0) || amt > free + 0.001 || (inv?.open != null && amt > inv.open + 0.001);

  return (
    <Dialog
      open
      onClose={onClose}
      title={t.docs.allocate}
      footer={
        <>
          <Button
            variant="primary"
            disabled={invalid}
            loading={busy}
            onClick={async () => {
              setBusy(true);
              try {
                await api(`/api/documents/${doc.id}/allocations`, { body: { invoiceId, amount: amt } });
                toast.success('השיוך נשמר');
                onDone();
                onClose();
              } catch (e) {
                toast.error(e);
              } finally {
                setBusy(false);
              }
            }}
          >
            שיוך
          </Button>
          <Button onClick={onClose}>{t.common.cancel}</Button>
        </>
      }
    >
      <div className="stack">
        <p className="muted">יתרה פנויה ב{doc.typeLabel} <Ltr>{doc.number}</Ltr>: <strong className="tabular">{fmtMoney(free)}</strong></p>
        <Field label="חשבונית פתוחה">
          <Select
            value={invoiceId ?? ''}
            onChange={(e) => {
              const v = Number(e.target.value) || null;
              setInvoiceId(v);
              const i = data?.items.find((d) => d.id === v);
              if (i?.open != null) setAmount(String(Math.min(free, i.open)));
            }}
          >
            <option value="">בחירת חשבונית…</option>
            {data?.items.map((d) => (
              <option key={d.id} value={d.id}>
                {d.number} · {fmtDate(d.issueDate)} · יתרה {fmtMoney(d.open)}
              </option>
            ))}
          </Select>
        </Field>
        {data && data.items.length === 0 ? <Callout tone="warn">אין ללקוח חשבוניות פתוחות.</Callout> : null}
        <Field label="סכום לשיוך">
          <Input inputMode="decimal" value={amount} onChange={(e) => setAmount(e.target.value)} ltr />
        </Field>
      </div>
    </Dialog>
  );
}
